import React, { useMemo, useRef } from 'react';
import { AlertTriangle } from 'lucide-react';
import { DebugSessionChat } from '../../components/conversations/DebugSessionChat.js';
import { extractJsonFromResponse } from '../../utils/contentParser.js';
import { useSaveSnapshot } from '../../hooks/useSnapshots.js';
import { StepDebugOutputPanel } from './StepDebugOutputPanel.js';
import { StepTemplatePicker } from './StepTemplatePicker.js';
import type { WorkflowBuilderStep } from './WorkflowBuilderTypes.js';
import type { AgentTemplateSummary } from '../../generated/api/types.gen.js';
import type { PreviewPromptRole } from './promptPreview.types.js';
import type { MultiStepAgentStream } from '../../hooks/useMultiStepAgentStream.js';

interface StepDebugTabProps {
  step: WorkflowBuilderStep;
  templateId: string | undefined;
  isNew: boolean;
  promptPreview: {
    systemPrompt: string;
    userPrompt: string;
    agentInput?: string;
    estimatedTokens: number;
    role: PreviewPromptRole;
  } | null;
  multiStream: MultiStepAgentStream;
  onResult: (stepId: string, result: unknown) => void;
  onFocusToggle: (focused: boolean) => void;
  onUpdateStep?: (stepId: string, updates: Partial<WorkflowBuilderStep>) => void;
  /** Optional roles list for resolving the currently selected template's title */
  roles?: AgentTemplateSummary[];
  /** Steps this step depends on, with their debug output status */
  dependencies?: Array<{ id: string; name: string; hasOutput: boolean }>;
  onApproveStep?: (stepId: string, snapshotId: number) => void;
  onRejectStep?: (stepId: string, notes: string) => void;
}

export const StepDebugTab: React.FC<StepDebugTabProps> = ({
  step,
  templateId,
  isNew,
  promptPreview,
  multiStream,
  onResult,
  onFocusToggle,
  onUpdateStep,
  roles,
  dependencies,
  onApproveStep,
  onRejectStep,
}) => {
  const saveSnapshot = useSaveSnapshot();
  const chatRef = useRef<HTMLDivElement>(null);

  const agentInput = promptPreview?.agentInput ?? '';
  const systemPrompt = promptPreview?.systemPrompt ?? '';

  const promptPreviewText = useMemo(() => {
    const parts: string[] = [];
    if (systemPrompt) parts.push(`[System]\n${systemPrompt}`);
    if (promptPreview?.userPrompt) parts.push(`[User]\n${promptPreview.userPrompt}`);
    return (
      parts.join('\n\n') || 'No prompts configured. Set a system prompt override or user prompt in the Prompt tab.'
    );
  }, [systemPrompt, promptPreview?.userPrompt]);

  const missingDeps = dependencies?.filter((d) => !d.hasOutput) ?? [];
  const templateIdNum = templateId ? Number(templateId) : undefined;

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Dependency status */}
      {missingDeps.length > 0 && (
        <div className="mx-3 mt-2 flex items-center gap-2 bg-warning-muted text-warning rounded-sm px-3 py-2 text-xs">
          <AlertTriangle size={14} className="shrink-0" />
          <span>
            Missing upstream output{missingDeps.length > 1 ? 's' : ''}:{' '}
            {missingDeps.map((d) => d.name || d.id).join(', ')}. Run {missingDeps.length > 1 ? 'them' : 'it'} first.
          </span>
        </div>
      )}

      {/* Agent Chat section */}
      <div ref={chatRef} className="flex-1 min-h-[420px]">
        {!step.templateSlug ? (
          <div className="flex flex-col items-center gap-3 py-8 px-4">
            <p className="text-sm text-muted">Select an agent template to start debugging this step.</p>
            <div className="w-full max-w-xs">
              <StepTemplatePicker
                step={step}
                roles={roles}
                onSelect={(r) =>
                  onUpdateStep?.(step.id.toString(), {
                    templateId: r?.id ?? null,
                    templateSlug: r?.slug ?? null,
                  })
                }
              />
            </div>
          </div>
        ) : !promptPreview ? (
          <div className="text-center py-8 text-muted text-sm">Loading prompt preview...</div>
        ) : (
          <DebugSessionChat
            session={multiStream.activeSession}
            actions={{
              execute: (params) => {
                multiStream.selectStep(step.id.toString());
                return multiStream.execute(step.id.toString(), params);
              },
              sendFollowUp: (msg) => multiStream.sendFollowUp(step.id.toString(), msg),
              stop: () => multiStream.stop(),
              pause: () => multiStream.pause(),
              resume: () => multiStream.resume(),
              reset: () => multiStream.reset(step.id.toString()),
            }}
            executeParams={{
              templateSlug: step.templateSlug ?? '',
              input: agentInput,
              systemPromptOverride: systemPrompt,
            }}
            resolvedUserPrompt={agentInput}
            promptPreviewText={promptPreviewText}
            onResult={(result) => {
              onResult(step.id.toString(), result);

              // Save as debug snapshot via new API
              if (!isNew && templateIdNum) {
                const extracted = typeof result === 'object' ? result : extractJsonFromResponse(String(result));
                const outputParts =
                  extracted && typeof extracted === 'object'
                    ? (extracted as Record<string, unknown>).outputParts
                    : undefined;

                saveSnapshot.mutate({
                  templateId: templateIdNum,
                  stepId: step.id.toString(),
                  stepName: step.name || '',
                  output: extracted ?? result,
                  fullText: typeof result === 'string' ? result : JSON.stringify(result),
                  outputParts: outputParts,
                  input: null,
                  contract: step.outputContract
                    ? {
                        outputContract: step.outputContract,
                        inputContracts: step.inputContracts,
                        negotiationStatus: step.contractNegotiationStatus ?? 'pending',
                        version: step.contractVersion ?? 0,
                      }
                    : undefined,
                });
              }
            }}
            onFocusToggle={onFocusToggle}
          />
        )}
      </div>

      {/* Debug output panel with visualization */}
      {!isNew && templateIdNum && (
        <StepDebugOutputPanel
          templateId={templateIdNum}
          stepId={step.id.toString()}
          contract={
            step.outputContract
              ? {
                  outputContract: step.outputContract,
                  inputContracts: step.inputContracts,
                  negotiationStatus: step.contractNegotiationStatus ?? 'pending',
                  version: step.contractVersion ?? 0,
                }
              : undefined
          }
          onRerun={() => chatRef.current?.scrollIntoView({ behavior: 'smooth' })}
          stepApproval={step.approvalStatus ? { status: step.approvalStatus } : undefined}
          onApprove={onApproveStep ? (snapshotId) => onApproveStep(step.id.toString(), snapshotId) : undefined}
          onReject={onRejectStep ? (notes) => onRejectStep(step.id.toString(), notes) : undefined}
          onPinAsContract={
            onUpdateStep
              ? (outputContract) =>
                  onUpdateStep(step.id.toString(), {
                    outputContract,
                    contractNegotiationStatus: 'overridden',
                    contractVersion: (step.contractVersion ?? 0) + 1,
                  })
              : undefined
          }
        />
      )}
    </div>
  );
};
